// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Most references to "CRM" mean hubspot, when this needs to be modularized,
// several parts must be recoded for flow purposes.
import { CRMConfiguration, Organization } from '@/account/api/types';
import { CRMConfigDialog } from '@/account/components/crm-config-dialog';
import { CRMPendingDialog } from '@/account/components/crm-pending-dialog';
import { LoadingIndicator } from '@/components/navigation-layout';
import { CloseRounded as CloseIcon } from '@/constants/hardfin-standard-icons';
import {
  INTEGRATION_META,
  findIntegrationConfig,
} from '@/utilities/integration';
import { Box, Dialog, DialogTitle, IconButton } from '@mui/material';
import { Suspense } from 'react';

interface Props {
  isOpen: boolean;
  organization: Organization;
  onClose: () => void;
  defaultTab?: number;
}

/**
 * This is a wrapper component for the CRM Dialog window
 * Buttons should be displayed/controlled by the child dialog that gets loaded
 * This component exists so that a suspense query can properly run at the correct DOM node
 * @param props
 * @constructor
 */
export function CRMDialog(props: Props) {
  // Find the CRM config (HubSpot or Salesforce) from the unified array
  const crmConfig: CRMConfiguration | null =
    findIntegrationConfig(
      props.organization.integrationConfigurations,
      'HUBSPOT'
    ) ??
    findIntegrationConfig(props.organization.integrationConfigurations, 'SFDC');

  const isFullyConnected =
    crmConfig !== null &&
    crmConfig.configurationState.allScopesPresent === true;

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth={true}
      PaperProps={{
        sx: {
          width: '50.25rem',
          height: isFullyConnected
            ? '40rem' // Enable the larger and static window for CRM
            : 'auto',
          maxWidth: '90vw',
          minWidth: '45rem',
          maxHeight: '90vh',
        },
      }}
      maxWidth={false}
    >
      <Suspense fallback={<LoadingIndicator />}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <DialogTitle>
            {crmConfig === null
              ? 'Connect CRM'
              : 'Configure ' +
                INTEGRATION_META[crmConfig.type].label +
                ' integration'}
          </DialogTitle>
          <IconButton
            onClick={props.onClose}
            sx={{ mr: 1 }}
            aria-label="close dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {!isFullyConnected ? (
          <CRMPendingDialog
            organization={props.organization}
            onClose={props.onClose}
          />
        ) : (
          <CRMConfigDialog
            organization={props.organization}
            integrationConfiguration={crmConfig}
            onClose={props.onClose}
            defaultTab={props.defaultTab}
          />
        )}
      </Suspense>
    </Dialog>
  );
}
